/**
 * Theme Service
 * Persiste la préférence Day/Night/System sur Firebase (user doc)
 * et fournit un fallback localStorage pour les utilisateurs non connectés.
 */

import { updateDocument, getDocument, subscribeToDocument } from './firestore.service';
import { auth } from './firebase/config';
import type { ThemePreference } from '../contexts/ThemeContext';

const THEME_FIELD = 'preferences.theme';

/**
 * Met à jour la préférence de thème dans le doc Firestore de l'utilisateur.
 * Ne fait rien si l'utilisateur n'est pas connecté.
 */
export const saveThemePreference = async (
  theme: ThemePreference,
  uid?: string | null
): Promise<void> => {
  const userId = uid ?? auth.currentUser?.uid;
  if (!userId) return;

  await updateDocument('users', userId, { [THEME_FIELD]: theme });
};

/**
 * Récupère la préférence de thème depuis Firestore.
 * Retourne null si l'utilisateur n'est pas connecté ou n'a pas de préférence.
 */
export const getThemePreference = async (
  uid?: string | null
): Promise<ThemePreference | null> => {
  const userId = uid ?? auth.currentUser?.uid;
  if (!userId) return null;

  const data = await getDocument<{ preferences?: { theme?: ThemePreference } }>('users', userId);
  return data?.preferences?.theme ?? null;
};

/**
 * Souscrit aux changements de préférence de thème sur Firestore.
 * Utile pour synchroniser entre appareils.
 */
export const subscribeThemePreference = (
  uid: string,
  onChange: (theme: ThemePreference | null) => void
) => {
  return subscribeToDocument<{ preferences?: { theme?: ThemePreference } }>(
    'users',
    uid,
    (data) => {
      onChange(data?.preferences?.theme ?? null);
    }
  );
};
